import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Stack,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    useGetUserQuery,
    useUpdateInflyxCreditStatsMutation,
    useGetInflyxScoreQuery,
    useUpdateInflyxRequestStatusMutation
} from "../../redux/api";
import CustomizedSnackbars from "../../components/snackbar";
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';

const RequestedInflyxCoinsDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: userDetails, isLoading: isUserLoading } = useGetUserQuery(id);
    const { data: scoreDetails, isLoading: isScoreLoading, refetch: refetchScore } = useGetInflyxScoreQuery(id);
    const [updateStats, { isLoading: isUpdating }] = useUpdateInflyxCreditStatsMutation();
    const [updateStatus, { isLoading: isStatusUpdating }] = useUpdateInflyxRequestStatusMutation();

    const [openEdit, setOpenEdit] = useState(false);
    const [editValues, setEditValues] = useState({
        inflyxScore: "",
        inflyxCoin: ""
    });

    const [snackbar, setSnackbar] = useState({
        message: "",
        severity: "success",
        open: false,
    });

    const [openConfirm, setOpenConfirm] = useState(false);

    const [confirmActionStatus, setConfirmActionStatus] = useState(null);

    const handleActionConfirmClose = () => {
        setConfirmActionStatus(null);
    };

    const handleActionConfirmProceed = () => {
        if (confirmActionStatus) {
            handleStatusUpdate(confirmActionStatus);
            setConfirmActionStatus(null);
        }
    };

    useEffect(() => {
        if (scoreDetails?.result) {
            setEditValues({
                inflyxScore: scoreDetails.result.inflyxScore || 0,
                inflyxCoin: scoreDetails.result.inflyxCoin || 0
            });
        }
    }, [scoreDetails]);

    const handleEditOpen = () => {
        // Ensure current values are set when opening
        if (scoreDetails?.result) {
            setEditValues({
                inflyxScore: scoreDetails.result.inflyxScore || 0,
                inflyxCoin: ""
            });
        }
        setOpenEdit(true);
    };

    const handleEditClose = () => {
        setOpenEdit(false);
    };

    const handleSaveClick = () => {
        setOpenConfirm(true);
    };

    const handleConfirmClose = () => {
        setOpenConfirm(false);
    };

    const handleConfirmSave = async () => {
        try {
            await updateStats({
                influencerId: id,
                inflyxScore: editValues.inflyxScore,
                inflyxCoin: editValues.inflyxCoin
            }).unwrap();

            setSnackbar({
                message: "Updated successfully",
                severity: "success",
                open: true
            });
            setOpenConfirm(false);
            setOpenEdit(false);
            refetchScore();
        } catch (error) {
            console.error("Update failed", error);
            setSnackbar({
                message: "Failed to update",
                severity: "error",
                open: true
            });
            setOpenConfirm(false);
        }
    };

    if (isUserLoading || isScoreLoading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;

    const influencer = userDetails?.result || {};
    const socialPlatforms = influencer.platform || [];

    const getIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'instagram': return <InstagramIcon color="primary" />;
            case 'youtube': return <YouTubeIcon color="error" />;
            case 'facebook': return <FacebookIcon color="primary" />;
            default: return null;
        }
    };



    const handleStatusUpdate = async (newStatus) => {
        try {
            await updateStatus({
                influencerId: id,
                status: newStatus
            }).unwrap();

            setSnackbar({
                message: `Request ${newStatus} successfully`,
                severity: "success",
                open: true
            });
            refetchScore();
        } catch (error) {
            console.error("Status update failed", error);
            setSnackbar({
                message: "Failed to update status",
                severity: "error",
                open: true
            });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <CustomizedSnackbars msgData={snackbar} setMsgData={setSnackbar} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
                <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                        Status: <span style={{
                            color: scoreDetails?.result?.status === 'Approved' ? 'green' :
                                scoreDetails?.result?.status === 'Rejected' ? 'red' : 'orange'
                        }}>{scoreDetails?.result?.status || 'Pending'}</span>
                    </Typography>
                    {(!scoreDetails?.result?.status || scoreDetails?.result?.status === 'Pending') && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => setConfirmActionStatus('Approved')}
                                disabled={isStatusUpdating}
                            >
                                Accept
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => setConfirmActionStatus('Rejected')}
                                disabled={isStatusUpdating}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    {/* {(scoreDetails?.result?.status === 'Rejected') && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleStatusUpdate('Pending')}
                            disabled={isStatusUpdating}
                        >
                            Reset to Pending
                        </Button>
                    )} */}
                </Box>
            </Box>
            <Typography variant="h4" gutterBottom>Requested Inflyx Coins - Details</Typography>

            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Avatar
                                src={influencer.picture}
                                alt={influencer.name}
                                sx={{ width: 120, height: 120 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md>
                            <Typography variant="h4" fontWeight="bold">{influencer.name}</Typography>
                            <Typography color="textSecondary" gutterBottom>{influencer.email}</Typography>
                            <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                                {socialPlatforms.map((platform, index) => (
                                    <Box key={index} display="flex" alignItems="center" gap={0.5} title={platform.name}>
                                        {getIcon(platform.platformType)}
                                        <Typography variant="body2">{platform.name || platform.platformType}</Typography>
                                    </Box>
                                ))}
                                {socialPlatforms.length === 0 && <Typography variant="body2" color="textSecondary">No social media linked</Typography>}
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">Inflyx Score</Typography>
                                <Typography variant="h4" color="primary" fontWeight="bold">
                                    {scoreDetails?.result?.inflyxScore != null ? scoreDetails.result.inflyxScore : "N/A"}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">Inflyx Coin</Typography>
                                <Typography variant="h4" color="secondary" fontWeight="bold">
                                    {scoreDetails?.result?.inflyxCoin != null ? scoreDetails.result.inflyxCoin : "N/A"}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box mt={3} display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            onClick={handleEditOpen}
                            size="large"
                            disabled={scoreDetails?.result?.status !== 'Approved'}
                            title={scoreDetails?.result?.status !== 'Approved' ? "Only approved requests can be edited" : ""}
                        >
                            Edit Score & Coins
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Details Shared by Influencer</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">No of Campaign</Typography>
                            <Typography variant="h6">
                                {scoreDetails?.result?.noOfCampaign || "N/A"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">Brand Name</Typography>
                            <Typography variant="h6">
                                {scoreDetails?.result?.brandName || "N/A"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">Avg Deal Value</Typography>
                            <Typography variant="h6">
                                {scoreDetails?.result?.avgDealValue || "N/A"}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Typography variant="h6" gutterBottom>Social Media Performance</Typography>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    {socialPlatforms.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Platform</TableCell>
                                        <TableCell>Handle</TableCell>
                                        <TableCell align="right">Followers</TableCell>
                                        <TableCell align="right">Avg. Likes</TableCell>
                                        <TableCell align="right">Avg. Views</TableCell>
                                        <TableCell align="right">Engagement Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {socialPlatforms.map((platform, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {getIcon(platform.platformType)}
                                                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                                                        {platform.platformType}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {platform.username ? platform.username : (platform.socialHandles && platform.socialHandles.length > 0
                                                    ? platform.socialHandles.map(h => h.decryptHandleName).join(", ")
                                                    : "N/A")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {platform.followers ? platform.followers.toLocaleString() : "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {platform.avgLikes ? Math.round(platform.avgLikes).toLocaleString() : "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {platform.avgViews ? Math.round(platform.avgViews).toLocaleString() : "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {platform.engagementRate ? (platform.engagementRate * 100).toFixed(2) + "%" : "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography color="textSecondary" align="center">No social media data available.</Typography>
                    )}
                </CardContent>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={openEdit} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Inflyx Score & Coins</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Inflyx Score"
                            type="number"
                            fullWidth
                            value={editValues.inflyxScore}
                            onChange={(e) => setEditValues({ ...editValues, inflyxScore: e.target.value })}
                            helperText="Calculated influence score"
                        />
                        <TextField
                            label="Inflyx Coin"
                            type="number"
                            fullWidth
                            value={editValues.inflyxCoin}
                            onChange={(e) => setEditValues({ ...editValues, inflyxCoin: e.target.value })}
                            helperText="Number of coins to add to this influencer's balance"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleEditClose} variant="outlined">Cancel</Button>
                    <Button onClick={handleSaveClick} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={handleConfirmClose}>
                <DialogTitle>Confirm Changes</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to save these changes to the Inflyx Score and Coins?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleConfirmClose} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmSave} variant="contained" autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Action Confirmation Dialog */}
            <Dialog open={Boolean(confirmActionStatus)} onClose={handleActionConfirmClose}>
                <DialogTitle>Confirm {confirmActionStatus === 'Approved' ? 'Accept' : 'Reject'}</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to {confirmActionStatus === 'Approved' ? 'accept' : 'reject'} this request?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleActionConfirmClose} variant="outlined">Cancel</Button>
                    <Button
                        onClick={handleActionConfirmProceed}
                        variant="contained"
                        color={confirmActionStatus === 'Rejected' ? 'error' : 'primary'}
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RequestedInflyxCoinsDetails;
